import SectionHeading from '../SectionHeading.jsx';
import CalendarMarkIcon from '../icons/CalendarMarkIcon.jsx';
import {
    formatDateLong,
    formatDateTime,
    formatTime,
} from '../../lib/DateTime.js';
import { useState } from 'react';
import CustomTags from '../customfields/CustomTags.jsx';
import { ObjectTypes } from '../../lib/ObjectTypes.js';
import OptionToggle from '~/components/OptionToggle.jsx';
import useBusinessPostApi from '~/lib/useBusinessPostApi.js';
import * as Toast from '~/lib/Toast.js';

const CancelSelectedEventsPrompt = ({ selectedEvents, onClose, refetch }) => {
    const [dontTriggerFlows, setDontTriggerFlows] = useState(false);

    const { save: saveCancel, isSaving: isCanceling } = useBusinessPostApi(
        `/calendar/events/0/cancel`,
    );

    const s = selectedEvents?.length > 1 ? 's' : '';

    function doCancel() {
        // Bulk cancel
        saveCancel({
            dontTriggerFlows,
            ids: selectedEvents,
        }).then(result => {
            onClose();
            Toast.success(
                result?.message || 'Successfully canceled appointments',
            );
            if (refetch) {
                refetch();
            }
        });
    }

    return (
        <div className="fixed bottom-0 left-0 right-0 w-full p-4 bg-red-500 text-white">
            <div className="font-medium text-center">
                Cancel {selectedEvents?.length} appointment{s}?
            </div>
            <div className="flex justify-center my-4">
                <OptionToggle
                    value={!dontTriggerFlows}
                    onChange={value => setDontTriggerFlows(!value)}
                    toggleFirst={true}
                    title="Send cancellation automations"
                />
            </div>

            <div className="flex items-center justify-center">
                <button
                    type="button"
                    className="btn-pill-white text-red-500 mr-4"
                    onClick={doCancel}
                >
                    {isCanceling ? 'Canceling...' : `Cancel Appointment${s}`}
                </button>
                <button
                    type="button"
                    className="btn-inverse-pill-white"
                    onClick={onClose}
                >
                    Go Back
                </button>
            </div>
        </div>
    );
};

export const EventSummary = ({ event, isFirst, onClick, contentLeft }) => {
    return (
        <div className="flex items-start">
            {!!contentLeft && contentLeft}
            <div
                className={`${!isFirst ? 'border-t' : ''} flex-1 py-4 pr-4 group cursor-pointer`}
                onClick={onClick}
            >
                <div className="flex justify-between">
                    <div>
                        <div className="text-lg group-hover:underline">
                            {event.event_type?.name}
                        </div>
                        <div className="text-gray-500 text-sm">
                            with {event.bookable_resource?.name}
                        </div>
                        <CustomTags
                            id={event.id}
                            type={ObjectTypes.BOOKING_EVENT}
                            fields={event.custom_fields}
                            className="mt-2"
                        />
                    </div>
                    {!!event.canceled_at && (
                        <div>
                            <div className="tag bg-red-500">Canceled</div>
                        </div>
                    )}
                </div>
                <div className="flex justify-between text-gray-500 text-sm mt-2">
                    <div>{formatDateLong(event.client_start)}</div>
                    <div>{formatTime(event.client_start)}</div>
                </div>
            </div>
        </div>
    );
};

const BookingEventList = ({
    events,
    onClick,
    className,
    allEventsTogether,
    beforeContent,
    showBulkEdit,
    refetch,
}) => {
    const now = new Date();
    const [showPast, setShowPast] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState([]);

    const upcomingEvents = !allEventsTogether
        ? events
              .filter(ev => new Date(ev.start) > now)
              .sort(ev => -new Date(ev.start).getTime())
        : [];
    const nowEvents = !allEventsTogether
        ? events.filter(
              ev => new Date(ev.start) < now && new Date(ev.end) > now,
          )
        : [];
    const pastEvents = !allEventsTogether
        ? events.filter(ev => new Date(ev.end) < now)
        : [];

    const allEvents = allEventsTogether ? events : [];

    const areAllSelected = upcomingEvents.length === selectedEvents.length;

    function doSelectAll() {
        if (areAllSelected) {
            setSelectedEvents([]);
        } else {
            setSelectedEvents(upcomingEvents.map(ev => ev.id));
        }
    }

    return (
        <div className={className}>
            <SectionHeading icon={<CalendarMarkIcon />} name="Appointments" />
            {beforeContent}
            {nowEvents?.length > 0 && (
                <>
                    <div className="panel-label mt-4">Now</div>
                    <div className="panel pl-4">
                        {nowEvents.map((event, idx) => (
                            <EventSummary
                                event={event}
                                isFirst={idx === 0}
                                key={event.id}
                                onClick={() => onClick(event)}
                            />
                        ))}
                    </div>
                </>
            )}

            {!allEventsTogether && (
                <>
                    <div className="flex items-center justify-between">
                        <div className="panel-label mt-4">Upcoming</div>
                        {showBulkEdit &&
                            upcomingEvents.filter(e => !e.canceled_at).length >
                                1 && (
                                <div className="mr-4">
                                    {!isEditing && (
                                        <button
                                            type="button"
                                            className="btn-link-sm"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            Edit
                                        </button>
                                    )}
                                    {isEditing && (
                                        <>
                                            <button
                                                type="button"
                                                className="btn-link-sm mr-2"
                                                onClick={() => doSelectAll()}
                                            >
                                                {areAllSelected
                                                    ? 'Deselect All'
                                                    : 'Select All'}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn-link-sm !text-red-600"
                                                onClick={() =>
                                                    setIsEditing(false)
                                                }
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                    </div>
                    <div className="panel pl-4">
                        {upcomingEvents.map((event, idx) => (
                            <EventSummary
                                event={event}
                                isFirst={idx === 0}
                                key={event.id}
                                onClick={() => onClick(event)}
                                contentLeft={
                                    isEditing &&
                                    !event.canceled_at && (
                                        <div className="w-8 mt-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedEvents.includes(
                                                    event.id,
                                                )}
                                                onChange={ev => {
                                                    if (ev.target.checked) {
                                                        setSelectedEvents(
                                                            old => [
                                                                ...old,
                                                                event.id,
                                                            ],
                                                        );
                                                    } else {
                                                        setSelectedEvents(old =>
                                                            old.filter(
                                                                id =>
                                                                    id !==
                                                                    event.id,
                                                            ),
                                                        );
                                                    }
                                                }}
                                            />
                                        </div>
                                    )
                                }
                            />
                        ))}
                        {upcomingEvents.length === 0 && (
                            <div className="text-gray-500 py-4">
                                There are no upcoming appointments
                            </div>
                        )}
                    </div>

                    <div className="panel-label mt-4">Past</div>
                    <div className="panel pl-4">
                        {(showPast || pastEvents.length <= 1) && (
                            <>
                                {pastEvents.map((event, idx) => (
                                    <EventSummary
                                        event={event}
                                        isFirst={idx === 0}
                                        key={event.id}
                                        onClick={() => onClick(event)}
                                    />
                                ))}
                            </>
                        )}
                        {!showPast && pastEvents.length > 1 && (
                            <a
                                href="#"
                                className="no-underline block py-4"
                                onClick={ev => {
                                    ev.preventDefault();
                                    setShowPast(true);
                                }}
                            >
                                Show {pastEvents.length} past appointments
                            </a>
                        )}
                        {pastEvents.length === 0 && (
                            <div className="py-4">
                                There are no past appointments
                            </div>
                        )}
                    </div>
                </>
            )}
            {allEventsTogether && (
                <div className="panel pl-4 mt-2">
                    {allEvents.map((event, idx) => (
                        <EventSummary
                            event={event}
                            isFirst={idx === 0}
                            key={event.id}
                            onClick={() => onClick(event)}
                        />
                    ))}
                </div>
            )}
            {selectedEvents.length > 0 && (
                <CancelSelectedEventsPrompt
                    selectedEvents={selectedEvents}
                    onClose={() => {
                        setSelectedEvents([]);
                        setIsEditing(false);
                    }}
                    refetch={refetch}
                />
            )}
        </div>
    );
};

export default BookingEventList;
